import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Star, Eye, EyeOff, CheckCircle2, XCircle, ExternalLink, Clock, MessageSquareQuote } from "lucide-react";
import { ReviewsDialog } from "@/components/admin/ReviewsDialog";
import api from "@/lib/api";
import { money } from "@/lib/site";
import { MentorAvatar } from "@/components/MentorCard";
import { MentorForm, EMPTY_MENTOR, toMentorPayload, fromMentor } from "@/components/MentorForm";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const errDetail = (e, f) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : f; };

const STATUS_STYLE = {
  published: "bg-emerald-100 text-emerald-700", pending: "bg-amber-100 text-amber-800",
  hidden: "bg-slate-100 text-slate-600", rejected: "bg-rose-100 text-rose-700",
};

const MentorDialog = ({ open, onClose, mentor, currency, onSaved }) => {
  const [form, setForm] = useState(EMPTY_MENTOR);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(mentor ? fromMentor(mentor) : EMPTY_MENTOR); }, [open, mentor]);

  const save = async () => {
    setBusy(true);
    try {
      if (mentor) await api.put(`/admin/mentors/${mentor.mentor_id}`, toMentorPayload(form));
      else await api.post("/admin/mentors", toMentorPayload(form));
      toast.success(mentor ? "Mentor updated" : "Mentor published");
      onSaved(); onClose();
    } catch (e) { toast.error(errDetail(e, "Could not save mentor")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="mentor-dialog">
        <DialogHeader><DialogTitle className="font-heading">{mentor ? `Edit ${mentor.name}` : "Add a mentor"}</DialogTitle>
          <DialogDescription>Profiles appear on the public mentors page. Pricing is charged via UPI.</DialogDescription></DialogHeader>
        <MentorForm form={form} setForm={setForm} currency={currency} idPrefix="admin-mentor" />
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-xl font-heading font-bold" data-testid="admin-mentor-save">{busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} {mentor ? "Save changes" : "Publish mentor"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const MentorRow = ({ m, currency, onEdit, onReviews, onChange }) => {
  const [busy, setBusy] = useState(false);
  const patch = async (data, msg) => {
    setBusy(true);
    try { await api.put(`/admin/mentors/${m.mentor_id}`, { ...toMentorPayload(fromMentor(m)), ...data }); toast.success(msg); onChange(); }
    catch (e) { toast.error(errDetail(e, "Update failed")); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${m.name}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/mentors/${m.mentor_id}`); toast.success("Mentor deleted"); onChange(); }
    catch (e) { toast.error(errDetail(e, "Delete failed")); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 flex flex-wrap items-center gap-4" data-testid={`admin-mentor-${m.mentor_id}`}>
      <MentorAvatar mentor={m} className="w-12 h-12 text-base" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="font-heading font-bold text-slate-900 truncate">{m.name}</p>
          <span className={`font-body text-[11px] px-2 py-0.5 rounded-full ${STATUS_STYLE[m.status] || STATUS_STYLE.hidden}`} data-testid={`mentor-status-${m.mentor_id}`}>{m.status}</span>
          {m.source === "application" && <span className="font-body text-[11px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">applied</span>}
          {m.featured && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}
        </div>
        <p className="font-body text-xs text-slate-500 truncate">{m.headline || "—"} · {m.email || "no email"}</p>
        <p className="font-body text-xs text-slate-400 mt-0.5">{m.session_price > 0 ? `${money(m.session_price, currency)} / session` : "no session"} · {m.monthly_price > 0 ? `${money(m.monthly_price, currency)} / month` : "no monthly"} · {(m.expertise || []).slice(0, 3).join(", ")}</p>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        {m.status === "pending" && (
          <>
            <Button size="sm" onClick={() => patch({ status: "published" }, "Approved & published")} disabled={busy} className="rounded-lg font-heading font-bold bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-mentor-${m.mentor_id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" onClick={() => patch({ status: "rejected" }, "Application rejected")} disabled={busy} className="rounded-lg" data-testid={`reject-mentor-${m.mentor_id}`}><XCircle className="w-4 h-4 mr-1" /> Reject</Button>
          </>
        )}
        {m.status === "published" && (
          <>
            <Button size="sm" variant="ghost" onClick={() => patch({ featured: !m.featured }, m.featured ? "Removed from featured" : "Featured on homepage")} disabled={busy} title="Feature" data-testid={`feature-mentor-${m.mentor_id}`}><Star className={`w-4 h-4 ${m.featured ? "text-amber-400 fill-amber-400" : ""}`} /></Button>
            <Button size="sm" variant="ghost" onClick={() => patch({ status: "hidden" }, "Hidden from directory")} disabled={busy} title="Hide" data-testid={`hide-mentor-${m.mentor_id}`}><EyeOff className="w-4 h-4" /></Button>
            <a href={`/mentors/${m.slug}`} target="_blank" rel="noreferrer" className="p-2 text-slate-500 hover:text-primary" title="View public page" data-testid={`view-mentor-${m.mentor_id}`}><ExternalLink className="w-4 h-4" /></a>
          </>
        )}
        {(m.status === "hidden" || m.status === "rejected") && (
          <Button size="sm" variant="ghost" onClick={() => patch({ status: "published" }, "Published")} disabled={busy} title="Publish" data-testid={`publish-mentor-${m.mentor_id}`}><Eye className="w-4 h-4" /></Button>
        )}
        <Button size="sm" variant="ghost" onClick={onReviews} title="Reviews" data-testid={`reviews-mentor-${m.mentor_id}`}><MessageSquareQuote className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" onClick={onEdit} title="Edit" data-testid={`edit-mentor-${m.mentor_id}`}><Pencil className="w-4 h-4" /></Button>
        <Button size="sm" variant="ghost" onClick={remove} className="text-slate-400 hover:text-destructive" title="Delete" data-testid={`delete-mentor-${m.mentor_id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const MentorsPanel = ({ currency = "INR" }) => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [reviewsFor, setReviewsFor] = useState(null);
  const load = () => api.get("/admin/mentors").then((r) => setRows(r.data)).catch(() => toast.error("Could not load mentors"));
  useEffect(() => { load(); }, []);

  const pending = (rows || []).filter((m) => m.status === "pending");
  const others = (rows || []).filter((m) => m.status !== "pending");
  return (
    <div data-testid="mentors-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-3 font-body text-sm text-slate-500">
          <span data-testid="mentors-count-published">{(rows || []).filter((m) => m.status === "published").length} published</span>
          <span className={pending.length ? "text-amber-700 font-semibold" : ""} data-testid="mentors-count-pending">{pending.length} pending review</span>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-xl font-heading font-bold" data-testid="add-mentor-btn"><Plus className="w-4 h-4 mr-1" /> Add mentor</Button>
      </div>
      {!rows ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
        <div className="space-y-6 mt-6">
          {pending.length > 0 && (
            <section>
              <h3 className="font-heading font-bold text-sm text-amber-800 flex items-center gap-1.5 mb-3"><Clock className="w-4 h-4" /> Applications awaiting review</h3>
              <div className="space-y-3">{pending.map((m) => <MentorRow key={m.mentor_id} m={m} currency={currency} onEdit={() => { setEditing(m); setOpen(true); }} onReviews={() => setReviewsFor(m)} onChange={load} />)}</div>
            </section>
          )}
          <section>
            {pending.length > 0 && <h3 className="font-heading font-bold text-sm text-slate-500 mb-3">All mentors</h3>}
            {others.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center font-body text-sm text-slate-500" data-testid="mentors-empty">No mentors yet. Add your first mentor to light up the directory.</div>
            ) : <div className="space-y-3">{others.map((m) => <MentorRow key={m.mentor_id} m={m} currency={currency} onEdit={() => { setEditing(m); setOpen(true); }} onReviews={() => setReviewsFor(m)} onChange={load} />)}</div>}
          </section>
        </div>
      )}
      <MentorDialog open={open} onClose={() => setOpen(false)} mentor={editing} currency={currency} onSaved={load} />
      <ReviewsDialog mentor={reviewsFor} open={!!reviewsFor} onClose={() => setReviewsFor(null)} />
    </div>
  );
};
